import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useSiteData } from '../context/SiteDataContext';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';

const Footer = () => {
  const { t } = useLanguage();
  const { companyInfo } = useSiteData();

  return (
    <footer className="bg-purple-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="flex items-center bg-white/10 px-4 py-2 rounded-lg">
                <span className="text-2xl font-serif text-white border-r-2 border-white/50 pr-2">S</span>
                <span className="text-2xl font-serif text-white pl-2">E</span>
              </div>
              <div>
                <h3 className="font-serif text-lg tracking-wider">Stockholm Elite</h3>
                <p className="text-purple-300 text-sm">Städservice</p>
              </div>
            </div>
            <p className="text-purple-200 text-sm leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t.footer.quickLinks}</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-purple-200 hover:text-orange-300 transition-colors text-sm">
                  {t.nav.home}
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-purple-200 hover:text-orange-300 transition-colors text-sm">
                  {t.nav.services}
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-purple-200 hover:text-orange-300 transition-colors text-sm">
                  {t.nav.about}
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-purple-200 hover:text-orange-300 transition-colors text-sm">
                  {t.nav.faq}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-purple-200 hover:text-orange-300 transition-colors text-sm">
                  {t.nav.contact}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t.footer.contactInfo}</h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-3 text-purple-200 hover:text-orange-300 transition-colors text-sm">
                  <div className="w-10 h-10 bg-purple-800 rounded-lg flex items-center justify-center">
                    <Phone size={18} />
                  </div>
                  {companyInfo.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 text-purple-200 hover:text-orange-300 transition-colors text-sm">
                  <div className="w-10 h-10 bg-purple-800 rounded-lg flex items-center justify-center">
                    <Mail size={18} />
                  </div>
                  {companyInfo.email}
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-purple-200 text-sm">
                  <div className="w-10 h-10 bg-purple-800 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} />
                  </div>
                  {companyInfo.address}
                </div>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-semibold text-lg mb-6">{t.footer.followUs}</h4>
            <div className="flex gap-4">
              <a
                href={companyInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-purple-800 hover:bg-orange-500 rounded-xl flex items-center justify-center transition-all hover:scale-110"
              >
                <Facebook size={22} />
              </a>
              <a
                href={companyInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-purple-800 hover:bg-orange-500 rounded-xl flex items-center justify-center transition-all hover:scale-110"
              >
                <Instagram size={22} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-purple-800">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <p className="text-purple-300 text-sm">
              {t.footer.rights}
            </p>
            <span className="hidden sm:inline text-purple-600">|</span>
            <p className="text-purple-300 text-sm">
              {t.footer.developedBy}{' '}
              <a 
                href="https://www.mitaict.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-orange-300 hover:text-orange-400 transition-colors font-medium"
              >
                MITA ICT
              </a>
            </p>
          </div>
          <div className="flex gap-6 text-sm">
            <Link to="/privacy" className="text-purple-300 hover:text-orange-300 transition-colors">
              {t.footer.privacy}
            </Link>
            <Link to="/cookies" className="text-purple-300 hover:text-orange-300 transition-colors">
              {t.footer.cookies}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
